import { Component, type ErrorInfo, type ReactNode } from "react";
import { armSelfHeal, HEALTHY_AFTER_MS, markHealthy } from "../lib/selfHeal";
import { clientErrorReporter } from "@shared/clientErrors";

/**
 * The last line of defence on an unattended screen (TIZEN-15, point 4).
 *
 * Until now the TV app had NO error boundary at all: a throw anywhere in the
 * render tree unmounted React and left a black panel, and on a platform where
 * we cannot reclaim the foreground that is the whole failure — nothing else in
 * the system notices, because the heartbeat dies with the page that sends it.
 * The device simply goes quiet and someone eventually drives to the room.
 *
 * So this catches the throw, says what happened where a support engineer will
 * find it, and arms a backed-off reload (see lib/selfHeal.ts for why the
 * constants are what they are).
 *
 * WHAT IT DELIBERATELY DOES NOT DO is render a friendly apology. A guest's
 * television showing a Foyer error card is a worse outcome than one showing
 * black for sixty seconds and then working: the card looks like a product
 * decision, the black screen looks like a fault, and only one of those gets
 * reported to the front desk. `fallback` exists for surfaces where an operator
 * would rather see something (the CMS preview), and is not used on a panel.
 */
interface Props {
  children: ReactNode;
  /** Optional visible fallback. Omit on a guest-facing panel - see above. */
  fallback?: ReactNode;
}

interface State {
  failed: boolean;
}

export default class SelfHealBoundary extends Component<Props, State> {
  state: State = { failed: false };
  private cancelHeal: (() => void) | null = null;
  private healthyTimer: number | null = null;

  static getDerivedStateFromError(): State {
    return { failed: true };
  }

  componentDidMount(): void {
    // Declare victory only after the app has genuinely stayed up. Clearing the
    // backoff at boot would make every reload look like the first one, which is
    // precisely how a crash loop hides from the thing counting it.
    if (typeof window === "undefined") return;
    this.healthyTimer = window.setTimeout(markHealthy, HEALTHY_AFTER_MS);
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Into the ring buffer, so `log_capture` carries it off the panel.
    console.error(
      `[self-heal] render failed: ${error.message}`,
      info.componentStack ?? ""
    );
    // And to the fleet-wide groups. The keepalive send survives the reload
    // armSelfHeal is about to schedule.
    clientErrorReporter()?.report(error, "render");
    this.cancelHeal = armSelfHeal(`render error: ${error.message}`);
  }

  componentWillUnmount(): void {
    this.cancelHeal?.();
    if (this.healthyTimer !== null && typeof window !== "undefined") {
      window.clearTimeout(this.healthyTimer);
    }
  }

  render(): ReactNode {
    if (this.state.failed) return this.props.fallback ?? null;
    return this.props.children;
  }
}
